import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Bot, Loader2, AlertCircle, Edit2, Check } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { openAIAssistantsService } from '../services/openai_assistants';
import { Message } from './MessageRenderer';
import { ChatSetupPanel } from './ChatSetupPanel';
import { ChatMessages } from './ChatMessages';
import { ChatInputPanel } from './ChatInputPanel';
import MessageLimitModal from './MessageLimitModal';

// Lazy load the BusinessReportManager component
const BusinessReportManager = lazy(() => import('./reports/BusinessReportManager').then(module => ({
  default: module.BusinessReportManager
})));

interface APIConfig {
  id: string;
  nombre: string;
  api_key: string;
  organization_id: string | null;
  entorno: string;
  estado: string;
}

interface OpenAIAssistant {
  id: string;
  name: string;
  description?: string;
  model: string;
  instructions?: string;
}

interface ChatInterfaceProps {
  onClose: () => void;
  selectedAssistant?: {
    id: string;
    nombre: string;
    descripcion: string;
    modelo: string;
  } | null;
}

interface Conversation {
  id: string;
  titulo: string;
  usuario_id: string;
  asistente_id: string;
  estado: 'activa' | 'archivada';
  created_at: string;
  updated_at: string;
  openai_thread_id?: string;
  conversation_text?: string;
  file_ids?: string[];
  file_metadata?: any;
}

function ChatInterface({ onClose, selectedAssistant }: ChatInterfaceProps) {
  const { user } = useAuthStore();
  const [apiConfigs, setApiConfigs] = useState<APIConfig[]>([]);
  const [selectedApiConfig, setSelectedApiConfig] = useState<APIConfig | null>(null);
  const [openAIAssistants, setOpenAIAssistants] = useState<OpenAIAssistant[]>([]);
  const [activeAssistant, setActiveAssistant] = useState<OpenAIAssistant | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(false);
  const [isConnectingAssistant, setIsConnectingAssistant] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCodeInterpreterPrompt, setShowCodeInterpreterPrompt] = useState(false);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [pendingMessage, setPendingMessage] = useState<{
    content: string;
    files: File[];
  } | null>(null);
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [canSendMessage, setCanSendMessage] = useState(true);
  const [messageLimit, setMessageLimit] = useState<{
    reached: boolean;
    current: number;
    max: number;
  }>({ reached: false, current: 0, max: 0 });
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [messageProcessing, setMessageProcessing] = useState(false);
  const [lastSentMessageId, setLastSentMessageId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState('Nueva Conversación');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      fetchApiConfigs();
      checkMessageLimit();
    }
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (selectedAssistant && apiConfigs.length > 0) {
      const config = apiConfigs[0];
      setSelectedApiConfig(config);
      handleConnect(config.id);
    }
  }, [selectedAssistant, apiConfigs]);

  useEffect(() => {
    if (selectedAssistant && openAIAssistants.length > 0) {
      const assistant = openAIAssistants.find(a => a.id === selectedAssistant.id);
      if (assistant) {
        setActiveAssistant(assistant);
        handleConnectAssistant(assistant);
      }
    }
  }, [selectedAssistant, openAIAssistants]);

  // Focus the title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  // Update conversation title in database when it changes
  const updateConversationTitle = async (newTitle: string) => {
    if (!currentConversation) return;
    
    try {
      const { error } = await supabase
        .from('conversaciones')
        .update({ 
          titulo: newTitle,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentConversation.id);
        
      if (error) throw error;
      
      // Update local state
      setCurrentConversation({
        ...currentConversation,
        titulo: newTitle
      });
      
      setSuccess('Título actualizado correctamente');
    } catch (error: any) {
      console.error('Error updating conversation title:', error);
      setError('Error al actualizar el título');
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConversationTitle(e.target.value);
  };

  const handleTitleSubmit = () => {
    const trimmedTitle = conversationTitle.trim();
    if (trimmedTitle) {
      updateConversationTitle(trimmedTitle);
    } else {
      setConversationTitle('Nueva Conversación');
      updateConversationTitle('Nueva Conversación');
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
      if (currentConversation) {
        setConversationTitle(currentConversation.titulo);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    event.target.value = '';
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const checkMessageLimit = async () => {
    if (!user?.id) return;

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'check_message_limit',
          data: {
            user_id: user.id
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error checking message limit:', errorData);
        // Default to allowing messages if there's an error
        setCanSendMessage(true);
        return;
      }

      const data = await response.json();
      setCanSendMessage(data.canSendMessage);
      setMessageLimit({
        reached: data.limitReached,
        current: data.current,
        max: data.max
      });

      if (data.limitReached) {
        setError(`Has alcanzado el límite de mensajes de tu suscripción ${data.subscription}.`);
      }
    } catch (error) {
      console.error('Error checking message limit:', error);
      // Default to allowing messages if there's an error
      setCanSendMessage(true);
    }
  };

  const fetchApiConfigs = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data: configs, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setApiConfigs(configs || []);
      console.log('Fetched API configs:', configs?.length || 0);

    } catch (error: any) {
      console.error('Error fetching API configs:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (configId: string) => {
    const config = apiConfigs.find(c => c.id === configId);
    if (!config) {
      setError('Configuración no encontrada');
      return;
    }

    try {
      setIsConnecting(true);
      setError('');
      setMessages([]);
      setOpenAIAssistants([]);
      setActiveAssistant(null);

      // Obtener API configuración
      const { data: apiConfig, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('id', configId)
        .single();

      if (error) throw error;
      if (!apiConfig) throw new Error('Configuración no encontrada');

      console.log('API client connection successful');
      setMessages([{
        role: 'system',
        content: `Conectado a ${config.nombre} (${config.entorno})`,
        created_at: new Date().toISOString()
      }]);

      await loadAssistants(config);
    } catch (error: any) {
      console.error('Error al conectar:', error);
      setError(error.message);
      setMessages([{
        role: 'error',
        content: `Error: ${error.message}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsConnecting(false);
    }
  };

  const loadAssistants = async (config: APIConfig) => {
    if (!config) {
      console.log('No API config passed');
      return;
    }

    console.log('Loading assistants for config:', config.nombre);

    try {
      setIsLoadingAssistants(true);
      const { data, error } = await supabase
        .from('asistentes')
        .select('*')
        .eq('estado', 'activo')
        .eq('api_configuracion_id', config.id)
        .order('nombre');

      if (error) throw error;

      const assistants = data.map((a: any) => ({
        id: a.id,
        name: a.nombre,
        description: a.descripcion,
        model: a.modelo,
        instructions: a.instructions
      }));

      console.log('Loaded assistants:', assistants.length);
      setOpenAIAssistants(assistants);
    } catch (error: any) {
      console.error('Error al cargar asistentes:', error);
      setError(error.message);
    } finally {
      setIsLoadingAssistants(false);
    }
  };

  const handleApiConfigSelect = (configId: string) => {
    const config = apiConfigs.find(c => c.id === configId);
    if (config) {
      console.log('API config selected:', config.nombre);
      setSelectedApiConfig(config);
      setOpenAIAssistants([]);
      setActiveAssistant(null);
      setMessages([]);
    }
  };

  const handleAssistantSelect = (assistantId: string) => {
    const assistant = openAIAssistants.find(a => a.id === assistantId);
    if (assistant) {
      console.log('Selected assistant:', assistant.name);
      setActiveAssistant(assistant);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Asistente seleccionado: ${assistant.name}`,
        created_at: new Date().toISOString()
      }]);
    }
  };

  const createNewConversation = async (assistantId: string): Promise<Conversation> => {
    try {
      if (!user) throw new Error('Usuario no autenticado');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`;

      const bodyData = {
        action: 'create_conversation',
        data: {
          user_id: user.id,
          assistant_id: assistantId,
          file_ids: [],
          file_metadata: []
        }
      };

      console.log('📤 Payload enviado a Supabase:\n', JSON.stringify(bodyData, null, 2));
      
      // Check message limit before creating conversation
      await checkMessageLimit();
      if (!canSendMessage) {
        setShowLimitModal(true);
        throw new Error(`Has alcanzado el límite de mensajes de tu suscripción (${messageLimit.current}/${messageLimit.max}).`);
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'chat-interface'
        },
        body: JSON.stringify(bodyData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.error && errorData.limitReached) {
          setCanSendMessage(false);
          setMessageLimit({
            reached: true,
            current: errorData.current || messageLimit.current,
            max: errorData.max || messageLimit.max
          });
          setShowLimitModal(true);
          throw new Error(errorData.error);
        }
        throw new Error(`Error creating conversation: ${response.status} ${response.statusText}`);
      }

      const { conversation } = await response.json();
      console.log('✅ Conversación creada:', conversation.id);
      setCurrentConversation(conversation);
      setConversationTitle(conversation.titulo);
      return conversation;

    } catch (error: any) {
      console.error('❌ Error creating conversation:', error);
      throw error;
    }
  };

  const handleConnectAssistant = async (assistant: OpenAIAssistant) => {
    try {
      console.log('Connecting to assistant:', assistant.name);
      setIsConnectingAssistant(true);
      setError('');

      const config = await openAIAssistantsService.getAssistantConfig(assistant.id);
      if (!config) throw new Error('No se pudo obtener la configuración del asistente');

      // Check message limit before creating conversation
      await checkMessageLimit();
      if (!canSendMessage) {
        setShowLimitModal(true);
        throw new Error(`Has alcanzado el límite de mensajes de tu suscripción (${messageLimit.current}/${messageLimit.max}).`);
      }

      // Create new conversation
      let conversation;
      try {
        conversation = await createNewConversation(assistant.id);
        if (!conversation) throw new Error('Error al crear la conversación');
      } catch (error: any) {
        console.error('Error creating conversation:', error);
        throw new Error(`Error al crear la conversación: ${error.message}`);
      }

      try {
        const { response, openai_thread_id, openai_message_id, file_ids } = await openAIAssistantsService.generateResponse(
          assistant.id,
          [{ role: 'system', content: 'Hola, presentate una sola vez' }]
        );

        console.log('Initial response received:', response ? 'yes' : 'no');

        // Update thread ID if needed
        if (openai_thread_id) {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
              'X-Client-Info': 'chat-interface'
            },
            body: JSON.stringify({
              action: 'update_thread_id',
              data: {
                conversation_id: conversation.id,
                thread_id: openai_thread_id
              }
            })
          });

          setCurrentConversation(prev => prev ? { ...prev, openai_thread_id } : prev);
        }

        // Add assistant response
        const assistantMessage: Message = {
          role: 'assistant',
          content: response,
          created_at: new Date().toISOString(),
          openai_message_id,
          openai_thread_id,
          file_ids
        };

        console.log('Adding assistant message to chat');
        setMessages(prev => [...prev, assistantMessage]);

        // Save assistant message
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
              'X-Client-Info': 'chat-interface'
            },
            body: JSON.stringify({
              action: 'add_message',
              data: {
                conversation_id: conversation.id,
                content: response,
                role: 'assistant',
                openai_message_id,
                openai_thread_id,
                file_ids,
                metadata: {
                  timestamp: new Date().toISOString()
                }
              }
            })
        });

        console.log('Assistant connection completed successfully');
        
        // Update message limit info after successful connection
        await checkMessageLimit();
      } catch (error: any) {
        console.error('Error generating initial response:', error);
        throw new Error(`Error al generar respuesta inicial: ${error.message}`);
      }
    } catch (error: any) {
      console.error('Error connecting to assistant:', error);
      setError(error.message);
      setMessages(prev => [...prev, {
        role: 'error',
        content: `Error: ${error.message}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsConnectingAssistant(false);
    }
  };

  const handleEndConversation = async () => {
    if (currentConversation) {
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'X-Client-Info': 'chat-interface'
          },
          body: JSON.stringify({
            action: 'archive_conversation',
            data: {
              conversation_id: currentConversation.id
            }
          })
        });
      } catch (error) {
        console.error('Error archiving conversation:', error);
      }
    }

    setMessages([{
      role: 'system',
      content: 'Conversación finalizada.',
      created_at: new Date().toISOString()
    }]);
    setInputMessage('');
    setActiveAssistant(null);
    setCurrentConversation(null);
    setSelectedFiles([]);
    setSelectedMessages(new Set());
    setMessageProcessing(false);
    setLastSentMessageId(null);
    setConversationTitle('Nueva Conversación');
    setIsEditingTitle(false);
  };

  const handleSendMessage = async () => {
    if (!activeAssistant || (!inputMessage.trim() && selectedFiles.length === 0) || !currentConversation) return;
    
    // Prevent sending if already processing a message
    if (messageProcessing) {
      console.log('Already processing a message, ignoring request');
      return;
    }

    // Check message limit before sending
    await checkMessageLimit();
    if (!canSendMessage) {
      setShowLimitModal(true);
      return;
    }

    if (selectedFiles.length > 0) {
      setPendingMessage({
        content: inputMessage.trim(),
        files: selectedFiles
      });
      setShowCodeInterpreterPrompt(true);
      return;
    }

    // Set processing flag to prevent duplicate sends
    setMessageProcessing(true);
    
    try {
      await sendMessage(inputMessage.trim(), selectedFiles, false);
    } finally {
      // Reset processing flag after completion (success or failure)
      setTimeout(() => {
        setMessageProcessing(false);
      }, 1000); // Add a small delay to prevent rapid re-clicks
    }
  };

  const handleCodeInterpreterChoice = async (useInterpreter: boolean) => {
    if (!pendingMessage) return;

    setShowCodeInterpreterPrompt(false);
    
    // Set processing flag to prevent duplicate sends
    setMessageProcessing(true);
    
    try {
      await sendMessage(pendingMessage.content, pendingMessage.files, useInterpreter);
      setPendingMessage(null);
    } finally {
      // Reset processing flag after completion
      setTimeout(() => {
        setMessageProcessing(false);
      }, 1000);
    }
  };

  const sendMessage = async (content: string, files?: File[], use_code_interpreter: boolean = false) => {
    if (!activeAssistant || !currentConversation) return;

    // Generate a unique ID for this message to track it
    const messageId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    // Check if we're trying to send the same message again
    if (lastSentMessageId === messageId) {
      console.log('Preventing duplicate message send');
      return;
    }
    
    // Store this message ID as the last sent
    setLastSentMessageId(messageId);

    try {
      setIsSending(true);
      setError('');

      const userMessage: Message = {
        id: messageId,
        role: 'user',
        content: content || 'Archivos adjuntos',
        created_at: new Date().toISOString(),
        files: files?.map(f => f.name)
      };

      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');

      // Save user message with custom metadata to help prevent double counting
      const messageResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'chat-interface'
        },
        body: JSON.stringify({
          action: 'add_message',
          data: {
            conversation_id: currentConversation.id,
            content: userMessage.content,
            role: 'user',
            metadata: {
              files: userMessage.files,
              timestamp: new Date().toISOString(),
              client_message_id: messageId,
              message_counted: true // Mark as counted at the client level
            }
          }
        })
      });

      if (!messageResponse.ok) {
        const errorData = await messageResponse.json();
        if (messageResponse.status === 400 && errorData.limitReached) {
          setCanSendMessage(false);
          setMessageLimit({
            reached: true,
            current: errorData.current || messageLimit.current,
            max: errorData.max || messageLimit.max
          });
          setShowLimitModal(true);
          throw new Error(errorData.error);
        }
        throw new Error(`Error sending message: ${messageResponse.status} ${messageResponse.statusText}`);
      }

      const { response, openai_thread_id, openai_message_id, file_ids } = await openAIAssistantsService.generateResponse(
        activeAssistant.id,
        messages
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map(msg => ({
            role: msg.role === 'user' ? 'user' : 'assistant',
            content: msg.content
          }))
          .concat([{
            role: 'user',
            content: userMessage.content
          }]),
        files,
        use_code_interpreter,
        currentConversation.openai_thread_id
      );

      // Update thread ID if needed
      if (openai_thread_id && openai_thread_id !== currentConversation.openai_thread_id) {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'X-Client-Info': 'chat-interface'
          },
          body: JSON.stringify({
            action: 'update_thread_id',
            data: {
              conversation_id: currentConversation.id,
              thread_id: openai_thread_id
            }
          })
        });

        setCurrentConversation(prev => prev ? { ...prev, openai_thread_id } : prev);
      }

      // Add assistant response
      const assistantMessage: Message = {
        role: 'assistant',
        content: response,
        created_at: new Date().toISOString(),
        openai_message_id,
        openai_thread_id,
        file_ids
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/conversation-manager`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'chat-interface'
        },
        body: JSON.stringify({
          action: 'add_message',
          data: {
            conversation_id: currentConversation.id,
            content: response,
            role: 'assistant',
            openai_message_id,
            openai_thread_id,
            file_ids,
            metadata: {
              timestamp: new Date().toISOString(),
              in_response_to: messageId
            }
          }
        })
      });

      setSelectedFiles([]);
      
      // Update message limit info
      await checkMessageLimit();

    } catch (error: any) {
      console.error('Error sending message:', error);
      setError(error.message);
      setMessages(prev => [...prev, {
        role: 'error',
        content: `Error: ${error.message}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsSending(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePrintConversation = async () => {
    // Get all messages content
    const allContent = messages
      .filter(m => m.role !== 'system' && m.role !== 'error')
      .map(m => `${m.role === 'user' ? 'Usuario' : 'Asistente'}: ${m.content}`)
      .join('\n\n');
    
    if (allContent) {
      try {
        // Dynamically import the components
        const [ReactDOMModule, BusinessReportManagerModule] = await Promise.all([
          import('react-dom/client'),
          import('./reports/BusinessReportManager')
        ]);
        
        // Create container for the report manager
        const reportManager = document.createElement('div');
        reportManager.id = 'report-manager-container';
        document.body.appendChild(reportManager);
        
        // Create root and render
        const root = ReactDOMModule.createRoot(reportManager);
        root.render(
          <Suspense fallback={<div>Cargando...</div>}>
            <BusinessReportManager 
              content={allContent}
              onClose={() => {
                root.unmount();
                document.body.removeChild(reportManager);
              }}
              conversationTitle={currentConversation?.titulo}
            />
          </Suspense>
        );
      } catch (error) {
        console.error('Error showing report manager:', error);
        setError('Error al generar el informe de la conversación');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span>Cargando configuraciones...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-4xl h-[90vh] rounded-lg flex flex-col">
        <div className="bg-white shadow-sm px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Bot className="w-6 h-6 text-blue-500" />
              {isEditingTitle ? (
                <div className="flex items-center">
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={conversationTitle}
                    onChange={handleTitleChange}
                    onBlur={handleTitleSubmit}
                    onKeyDown={handleTitleKeyDown}
                    className="text-xl font-semibold text-gray-800 border-b border-blue-500 focus:outline-none px-1 py-0.5"
                    placeholder="Título de la conversación"
                  />
                  <button 
                    onClick={handleTitleSubmit}
                    className="ml-2 p-1 text-blue-500 hover:bg-blue-50 rounded-full"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center">
                  <h1 className="text-xl font-semibold text-gray-800">{currentConversation?.titulo || conversationTitle}</h1>
                  {currentConversation && (
                    <button 
                      onClick={() => setIsEditingTitle(true)}
                      className="ml-2 p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full"
                      title="Editar título"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                handleEndConversation();
                onClose();
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
          {!selectedAssistant && (
            <ChatSetupPanel
              apiConfigs={apiConfigs}
              selectedApiConfig={selectedApiConfig}
              openAIAssistants={openAIAssistants}
              activeAssistant={activeAssistant}
              isConnecting={isConnecting}
              isLoadingAssistants={isLoadingAssistants}
              isConnectingAssistant={isConnectingAssistant}
              onApiConfigSelect={handleApiConfigSelect}
              onConnect={handleConnect}
              onLoadAssistants={() => selectedApiConfig && loadAssistants(selectedApiConfig)}
              onAssistantSelect={handleAssistantSelect}
              onConnectAssistant={handleConnectAssistant}
            />
          )}
        </div>

        <ChatMessages 
          messages={messages} 
          messagesEndRef={messagesEndRef} 
          conversationTitle={currentConversation?.titulo}
        />

        <ChatInputPanel
          inputMessage={inputMessage}
          selectedFiles={selectedFiles}
          isSending={isSending}
          isConnecting={isConnecting}
          isLoadingAssistants={isLoadingAssistants}
          activeAssistant={activeAssistant}
          inputRef={inputRef}
          onInputChange={setInputMessage}
          onFileClick={handleFileClick}
          onSendMessage={handleSendMessage}
          onEndConversation={handleEndConversation}
          onKeyPress={handleKeyPress}
          onFileRemove={handleRemoveFile}
          onPrintConversation={handlePrintConversation}
        />

        {messageLimit.reached && (
          <div className="bg-red-100 border-t border-red-300 p-3 text-red-700 text-sm">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>
                Has alcanzado el límite de mensajes de tu suscripción. 
                <button 
                  onClick={() => setShowLimitModal(true)}
                  className="ml-2 underline font-medium"
                >
                  Más información
                </button>
              </span>
            </div>
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          multiple
          accept=".txt,.md,.csv,.json,.xml,.html,.htm,.css,.js,.jsx,.ts,.tsx,.py,.rb,.php,.java,.c,.cpp,.h,.hpp,.sql,.log,.yml,.yaml,.toml,.ini,.env,.sh,.bash,.zsh,.ps1,.bat,.cmd,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.png,.jpg,.jpeg,.gif"
        />

        {showCodeInterpreterPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">¿Deseas activar el intérprete de código?</h3>
              <p className="text-gray-600 mb-6">
                El intérprete de código permite al asistente analizar y procesar los archivos adjuntos.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => handleCodeInterpreterChoice(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  No
                </button>
                <button
                  onClick={() => handleCodeInterpreterChoice(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Sí
                </button>
              </div>
            </div>
          </div>
        )}

        {showLimitModal && (
          <MessageLimitModal 
            currentCount={messageLimit.current}
            maxCount={messageLimit.max}
            onClose={() => setShowLimitModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default ChatInterface;